import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramDuctArea } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.ductArea.v1';

type Shape = 'rectangular' | 'circular';
type Fields = {
  shape: Shape;
  width: string;
  height: string;
  diameter: string;
  area: string;
};
const EMPTY: Fields = { shape: 'rectangular', width: '', height: '', diameter: '', area: '' };

/**
 * Duct Area — rectangular A = W × H or circular A = π · D² / 4.
 * Width/height/diameter in length units (ft or m), area in area units (ft² or m²).
 */
const AirflowDuctAreaScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    if (fields.shape === 'rectangular') {
      const filled = [fields.width, fields.height, fields.area].filter((v) => v !== '').length;
      if (filled < 2) {
        setError('Fill any 2 of the 3 fields to solve for the missing one.');
        return;
      }
      if (filled === 3) {
        setError('All 3 values are filled — clear one to solve for it.');
        return;
      }
      const w = toImp(fields.width, 'length');
      const h = toImp(fields.height, 'length');
      const a = toImp(fields.area, 'area');
      if (fields.area === '') {
        if (w <= 0 || h <= 0) {
          setError('Width and height must be greater than zero.');
          return;
        }
        setSolvedFor('area');
        set({ area: fromImp(w * h, 'area', 2) });
      } else if (fields.width === '') {
        if (h <= 0) {
          setError('Height must be greater than zero.');
          return;
        }
        setSolvedFor('width');
        set({ width: fromImp(a / h, 'length', 2) });
      } else if (fields.height === '') {
        if (w <= 0) {
          setError('Width must be greater than zero.');
          return;
        }
        setSolvedFor('height');
        set({ height: fromImp(a / w, 'length', 2) });
      }
    } else {
      const filled = [fields.diameter, fields.area].filter((v) => v !== '').length;
      if (filled < 1) {
        setError('Fill at least 1 of the 2 fields to solve for the missing one.');
        return;
      }
      if (filled === 2) {
        setError('All 2 values are filled — clear one to solve for it.');
        return;
      }
      const d = toImp(fields.diameter, 'length');
      const a = toImp(fields.area, 'area');
      if (fields.area === '') {
        if (d <= 0) {
          setError('Diameter must be greater than zero.');
          return;
        }
        setSolvedFor('area');
        set({ area: fromImp(Math.PI * Math.pow(d / 2, 2), 'area', 2) });
      } else if (fields.diameter === '') {
        if (a <= 0) {
          setError('Area must be greater than zero.');
          return;
        }
        setSolvedFor('diameter');
        set({ diameter: fromImp(Math.sqrt(a / Math.PI) * 2, 'length', 2) });
      }
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() =>
    fields.shape === 'rectangular'
      ? ([
          fields.width && { label: 'Width', value: fields.width, unit: unit('length'), emphasis: solvedFor === 'width' },
          fields.height && { label: 'Height', value: fields.height, unit: unit('length'), emphasis: solvedFor === 'height' },
          fields.area && { label: 'Area', value: fields.area, unit: unit('area'), emphasis: solvedFor === 'area' },
        ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[])
      : ([
          fields.diameter && { label: 'Diameter', value: fields.diameter, unit: unit('length'), emphasis: solvedFor === 'diameter' },
          fields.area && { label: 'Area', value: fields.area, unit: unit('area'), emphasis: solvedFor === 'area' },
        ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[]), [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Duct Area"
      subtitle="Calculate duct cross-sectional area for rectangular or circular ducts"
      formula={
        <>
          <FormulaLine>Rectangular: A = W × H</FormulaLine>
          <FormulaLine>Circular: A = π × (D / 2)²</FormulaLine>
          <FormulaNote>
            A = Cross-sectional area ({unit('area')}), W/H = Duct dimensions ({unit('length')}), D = Duct diameter ({unit('length')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: A = W × H  |  A = π × (D/2)²"
      diagram={<DiagramDuctArea />}
      outputSlots={{
        area_sqft: fields.area ? toImp(fields.area, 'area') : undefined,
        duct_width_in:
          fields.shape === 'rectangular' && fields.width ? toImp(fields.width, 'length') * 12 : undefined,
        duct_height_in:
          fields.shape === 'rectangular' && fields.height ? toImp(fields.height, 'length') * 12 : undefined,
        duct_diameter_in:
          fields.shape === 'circular' && fields.diameter ? toImp(fields.diameter, 'length') * 12 : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.area_sqft != null && !fields.area) {
          patch.area = fromImp(values.area_sqft, 'area', 2);
        }
        if (values.duct_width_in != null && !fields.width) {
          patch.width = fromImp(values.duct_width_in / 12, 'length', 2);
          patch.shape = 'rectangular';
        }
        if (values.duct_height_in != null && !fields.height) {
          patch.height = fromImp(values.duct_height_in / 12, 'length', 2);
          patch.shape = 'rectangular';
        }
        if (values.duct_diameter_in != null && !fields.diameter) {
          patch.diameter = fromImp(values.duct_diameter_in / 12, 'length', 2);
          patch.shape = 'circular';
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <SegmentedButtons
        value={fields.shape}
        onValueChange={(v) => set({ shape: v as Shape })}
        buttons={[
          { value: 'rectangular', label: 'Rectangular' },
          { value: 'circular', label: 'Circular' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {fields.shape === 'rectangular' ? (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField
              label={`Width (${unit('length')})`}
              value={fields.width}
              onChangeText={(v) => set({ width: v })}
              rightText={unit('length')}
              onSubmitEditing={calculate}
            />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField
              label={`Height (${unit('length')})`}
              value={fields.height}
              onChangeText={(v) => set({ height: v })}
              rightText={unit('length')}
              onSubmitEditing={calculate}
            />
          </View>
        </>
      ) : (
        <View style={{ marginBottom: 12 }}>
          <NumericField
            label={`Diameter (${unit('length')})`}
            value={fields.diameter}
            onChangeText={(v) => set({ diameter: v })}
            rightText={unit('length')}
            onSubmitEditing={calculate}
          />
        </View>
      )}

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Area (${unit('area')})`}
          value={fields.area}
          onChangeText={(v) => set({ area: v })}
          rightText={unit('area')}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowDuctAreaScreen;
